import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

import { VNC_PORT } from './computer-use';

export interface PortPreview {
  url: string;
  token: string;
}

/** `base` is the sandbox resource prefix — see `sandbox-files.ts` for details. */
/** SWR key for a session's listening ports, so callers can revalidate it after starting a server. */
export function portsKey(base: string): string {
  return `${base}/ports`;
}

export function useSandboxPorts(base: string | null) {
  const key = base ? portsKey(base) : null;
  const { data, error, isLoading } = useSWR(
    key,
    async () => {
      const result = await fetchEndpointData<{ ports: number[] }>(key!);
      // Daytona's toolbox returns these in no particular order, so without this
      // the table reshuffles on every poll. Sorting at the cache boundary rather
      // than in the component means it happens once per fetch, the sorted array
      // is what SWR caches, and every consumer gets the same guarantee.
      return { ports: result.ports.sort((a, b) => a - b) };
    },
    {
      refreshInterval: 5000,
      revalidateOnFocus: false,
    },
  );
  return { ports: data?.ports ?? [], isLoading, error };
}

export async function getPortPreview(base: string, port: number): Promise<PortPreview> {
  return fetchEndpointData<PortPreview>(`${base}/ports/preview?port=${port}`);
}

/** Revoke a signed preview URL so it 401s immediately. Needs the `token` handed
 *  back by {@link getPortPreview} — Daytona expires links by token, not by port. */
export async function expirePortPreview(base: string, port: number, token: string): Promise<void> {
  await fetchEndpointData(`${base}/ports/preview/expire`, { method: 'POST', body: { port, token } });
}

/**
 * Same-origin URL for the Preview tab's iframe.
 *
 * Not the signed Daytona URL the rest of this file deals in: the preview proxy
 * (`backend/.../routes/agent_sessions/preview.rs`) fetches that page and serves
 * it from our own origin with an element-selection agent injected, which is the
 * only way the parent frame can talk to it at all.
 */
export function previewProxyUrl(base: string, port: number, path = '/'): string {
  const { pathname, query } = splitPath(path);
  // `__zooid_port` goes first because the proxy pulls it out of the raw query string and
  // forwards whatever is left to the app untouched (`split_port_param` in preview.rs).
  const search = `?__zooid_port=${port}${query ? `&${query}` : ''}`;
  return pathname ? `${base}/preview/${pathname}${search}` : `${base}/preview${search}`;
}

/**
 * Normalise a hand-typed path into what the proxy's `/preview/{*path}` route expects.
 *
 * Deliberately forgiving: someone typing into a path bar writes `aboutus`, `/aboutus` or
 * `/aboutus?tab=1` interchangeably, and none of those should be a dead end. Only the leading
 * slash is stripped — the rest is passed through verbatim rather than re-encoded, since the
 * segments may already be encoded and doing it twice turns `%20` into `%2520`.
 */
function splitPath(path: string): { pathname: string; query: string } {
  const trimmed = path.trim().replace(/^\/+/, '');
  const queryAt = trimmed.indexOf('?');
  if (queryAt === -1) return { pathname: trimmed, query: '' };
  return { pathname: trimmed.slice(0, queryAt), query: trimmed.slice(queryAt + 1) };
}

/** Display form of a path: always leading-slashed, so the bar never shows a bare `aboutus`. */
export function normalizePreviewPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export interface PortInfo {
  label: string;
  /** Sandbox infrastructure rather than something the user started, so the UI can
   *  say so — otherwise the desktop's own ports read as unexplained strangers. */
  system?: boolean;
}

/** Cosmetic labels for common dev-tool ports — purely a display nicety, not authoritative:
 * the port list itself always comes from what's actually listening in the sandbox. */
const WELL_KNOWN_PORTS: Record<number, PortInfo> = {
  3000: { label: 'Dev server' },
  3333: { label: 'Frontend dev server' },
  4444: { label: 'API server' },
  5173: { label: 'Vite dev server' },
  5432: { label: 'PostgreSQL' },
  5503: { label: 'PostgreSQL' },
  5901: { label: 'VNC server', system: true },
  // Keyed off the constant the Desktop tab actually connects to, so the two can't drift.
  [VNC_PORT]: { label: 'Desktop (noVNC)', system: true },
  6379: { label: 'Redis' },
  8000: { label: 'HTTP server' },
  8025: { label: 'Mailpit' },
  8080: { label: 'HTTP server (alt)' },
};

export function portInfo(port: number): PortInfo | null {
  return WELL_KNOWN_PORTS[port] ?? null;
}
